<template>

  <div :key="form_id">

    <div v-if="!loading">
      <RegistrationSearchModal
          v-if="registrationSearchEnabled"
          :form="myForm"
          @hide-parent-form="hideForm"
          @show-parent-form="showForm"
          @setFormValues="setFormValues"></RegistrationSearchModal>
    </div>
    <div v-if="renderForm">

      <div class="pt-6 px-md-8 px-4" v-if="loading">
        <v-container fluid>
          <v-row>
            <v-col class="pa-0">
              <v-skeleton-loader type="text@2, sentences, text@3, image, text@3"></v-skeleton-loader>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <FormulateForm class="formulate-style-compact" v-if="!loading" @submit="sendHandler" @submit-raw="saveHandler" @failed-validation="onValidationErrors" v-model="values" name="fillForm">

        <v-alert
            class="ma-4"
            v-if="clientCredits < 1"
            dense
            prominent
            type="warning"
        >
          De credits zijn op. U kunt dit formulier wel versturen, maar de registratie blijft verborgen totdat de credits zijn aangevuld.
        </v-alert>

        <v-alert
            color="secondary"
            dense
            text
            v-if="hasUnprocessedRegistrations"
            class="ma-8">
          Er worden momenteel nog registraties verwerkt. De gegevens van de kaart en/of zoek databron kunnen hierdoor mogelijk niet volledig up-to-date zijn.
        </v-alert>

        <div class="px-md-8 px-4">
          <v-container fluid>
            <v-row v-for="item in conditionalSchema" :key="item.uuid">
              <v-col class="pa-0">
                <eform-element
                    :element="item"

                    v-show="isVisuallyShown(item)"
                    :values="values"
                    :schema="schema"
                    :form-settings="formSettings"
                    @subform-opened="subformOpened"
                    @subform-closed="subformClosed"
                    @subform-values-changed="subformValuesChanged"
                />
              </v-col>
            </v-row>

            <p v-if="!schema.length">Er zijn nog geen velden toegevoegd aan dit formulier.</p>

          </v-container>
        </div>
      </FormulateForm>
    </div>

    <div style="height: 100px;"></div> <!-- Needed to offset the v-footer -->
    <v-footer padless inset :absolute="form_in_modal" :fixed="!form_in_modal" :app="!form_in_modal" class="eform-footer-transparant py-sm-2">
      <v-container fluid class="mx-md-4">
        <v-btn
            v-if="appOnline"
            :disabled="disableSubmit"
            color="primary"
            depressed
            @click="btnSentRegistration('fillForm')"
        >
          Versturen
        </v-btn>
        <v-btn
            v-if="!guest_form"
            :disabled="disableSubmit"
            outlined
            color="secondary"
            @click="btnSaveRegistration('fillForm')"
            class="ml-3"
        >
          Opslaan
        </v-btn>
        <v-btn
            v-if="!guest_form && userHasRole('admin')"
            :disabled="disableSubmit"
            outlined
            color="secondary"
            @click="saveRegistrationViaAutosave('fillForm')"
            class="ml-3"
        >
          Autosave
        </v-btn>
        <v-progress-circular v-if="savingForm" indeterminate color="secondary" class="ml-2"></v-progress-circular>
      </v-container>
    </v-footer>

    <!-- Progress/saving modal when sending the registration -->
    <form-fill-saving-modal
        :show-modal="savingModal"
        :send-registration-phase="sendRegistrationPhase"
        :send-registration-progress="sendRegistrationProgress"
    ></form-fill-saving-modal>

  </div>

</template>
<script>
import EformElement from '@/components/EformElement';
import {mapActions, mapGetters} from "vuex";
import axios from "axios";
import eformConditionals from "@/mixins/eformConditionals";
import formulateComplexFileData from "@/mixins/formulateComplexFileData";
import clientHelpers from "@/mixins/clientHelpers";
import RegistrationSearchModal from "../../components/search/RegistrationSearchModal";
import eformHelpers from "../../mixins/eformHelpers";
import Vue from "vue";
import FormFillSavingModal from "@/views/forms/FormFillSavingModal.vue";
import eformSender from "@/mixins/eformSender";
import userHelpers from "@/mixins/userHelpers";
import _, {cloneDeep, has} from "lodash";
import store from "@/plugins/store";
import moment from "moment/moment";
import {uuid} from "vue-uuid";

export default {
  name: "FormFillCore",
  components: {
    FormFillSavingModal,
    RegistrationSearchModal,
    EformElement,
  },
  mixins: [
    formulateComplexFileData,
    eformConditionals,
    eformSender,
    eformHelpers,
    userHelpers,
    clientHelpers,
  ],
  props: {
    form: {
      type: Object,
      required: true,
    },
    /**
     * Indicates if the form is used as a guest form.
     */
    guest_form: {
      type: Boolean,
      default: false,
    },
    /**
     * The registration ID of a saved (unsend) registration.
     */
    saved_registration_id: {
      type: String,
      required: false,
    },
    /**
     * Registration ID is used to edit a registration. Used to reload an existing registration and used to load a workflow registration.
     */
    registration_id: {
      type: String,
      required: false,
    },
    form_in_modal: {
      type: Boolean,
      default: false,
    },
    force_reload: {
      type: String,
      default: '0',
    }
  },
  data() {
    return {
      form_id: null,
      sendMethod: '', // Can be 'send' or 'save-manual' or 'save-autosave'
      autoSavePolling: null,
      autoSaveStartPollingOnTimestamp: null,
      subformModalIsOpen: false,
      autoSaveCooldown: Date.now() + 6000, // Execute the first autosave after 60 seconds
      hasUnprocessedRegistrations: false,
      formKey: uuid.v4(),
      loadingForm: true,
      savingForm: false,
      myForm: null,
      renderForm: true,
      loadingSavedRegistration: false,
      savingModal: false,
      sendRegistrationProgress: 0,
      sendRegistrationPhase: 'idle', // Can be: idle, sending, server-processing
      savingComplete: false,
      showError: false,
      unsavedChanges: true,
      subformHasClosedRecently: false,
    }
  },
  created() {
    this.form_id = this.form.form_id;
  },
  async mounted() {
    // Get form from state
    await this.initializeForm(this.form);
    // Save timestamp when form is initialised. Used to delay the start of the polling
    this.autoSaveStartPollingOnTimestamp = Date.now() + 1000;

    // Global keyboard shortcut: Ctrl+U -> autosave
    window.addEventListener('keydown', this.onGlobalKeydown);
  },
  computed: {
    ...mapGetters(["appOnline", "getFormByID"]),
    registrationSearchEnabled() {
      return _.get(this.myForm.settings, 'search_enabled', false) === true;
    },
    loading() {
      if (this.loadingForm || this.loadingSavedRegistration) {
        return true;
      }
      return false;
    },
    disableSubmit() {
      if (this.loading || this.savingForm || this.appOnline === false || this.subformHasClosedRecently) {
        return true;
      }
      else {
        return false;
      }
    },
    values: {
      get() {
        // Note: Don't add any new keys to the values here, it will break reactivity and the form rules won't be executed on FormFill.vue
        return this.myForm.values
      },
      set(values) {
        this.myForm.values = values;
      }
    },
    schema: {
      get() {
        if (!has(this.myForm, 'schema')) {
          return [];
        }
        return this.myForm.schema;
      }
    },
    rules: {
      get() {
        return this.myForm.rules;
      }
    },
    formName() {
      if (this.myForm) {
        return this.myForm.form_name ? this.myForm.form_name : ''
      }
      else {
        return 'Laden..'
      }
    },
    // Use this to send some extra data/settings to the form element.
    formSettings() {
      if (_.has(this.myForm.settings, 'bad_internet_mode')) {
        return {
          bad_internet_mode: this.myForm.settings.bad_internet_mode,
        }
      }
      else {
        return {}
      }
    },
    formType() {
      if (this.myForm) {
        return this.myForm.form_type ? this.myForm.form_type : ''
      }
      return '';
    }
  },
  watch: {
    'myForm.values': {
      deep: true,
      handler() {
        // Start auto save polling
        this.startAutosave();
      }
    },
  },
  methods: {
    ...mapActions(["registrationCompleted"]),
    btnSentRegistration(formName) {
      let registration_info = this.getBasicRegistrationInfo(this.myForm);
      Vue.prototype.$rollbar.info('Eform registration start sending to server.', registration_info);
      // Set flag
      this.sendMethod = 'send'
      // Trigger submit of named Vue Formulate form
      this.$formulate.submit(formName);
    },
    btnSaveRegistration(formName) {
      let registration_info = this.getBasicRegistrationInfo(this.myForm);
      Vue.prototype.$rollbar.info('Eform registration start saving to local device.', registration_info);
      // Set flags
      this.sendMethod = 'save-manual'
      // Trigger submit of named Vue Formulate form
      this.$formulate.submit(formName);
    },
    saveRegistrationViaAutosave(formName) {
      // Set flags
      this.sendMethod = 'save-autosave'
      // Trigger submit of named Vue Formulate form
      this.$formulate.submit(formName);
      // Add this if you want to remove the validation errors that show up when auto-saving the form.
      // this.$formulate.resetValidation(formName);
    },
    // Check if form has unprocessed registrations
    async checkFormUnprocessedRegistrations() {
      // Only check if form schema has 'map' or 'search' elements
      let hasMapOrSearchElements = this.schema.some(element => element.elementType === 'map' || element.elementType === 'search');
      if (!hasMapOrSearchElements) {
        this.hasUnprocessedRegistrations = false;
        return;
      }

      // Fetch results form the API
      await axios
          .get('api/form/' + this.form_id + '/has-unprocessed-registrations')
          .then(response => {
            this.hasUnprocessedRegistrations = response.data.has_unprocessed_registrations;
          })
          .catch(error => {
            console.log(error)
          })
    },
    /**
     * This submit handler will validate the form and send the registration to the server.
     *
     * @param formValues
     */
    sendHandler(formValues) {
      if (this.sendMethod !== 'send') {
        return; // Saving registrations is handled in this.saveHandler()
      }
      this.prepareAndSendRegistration(formValues, this.guest_form)
    },
    /**
     * This raw submit handler will not validate the form and will only save the registration.
     *
     * @param formSubmission
     * @returns {Promise<void>}
     */
    async saveHandler(formSubmission) {
      if (!['save-manual', 'save-autosave'].includes(this.sendMethod)) {
        return; // Sending registrations is handled in this.sendHandler()
      }

      this.savingForm = true;

      let formValues = await formSubmission.values()

      // Only show saving modal when the user manually saved the form
      if (this.sendMethod === 'save-manual') {
        this.savingModal = true
      }

      // Prepare the registration for saving
      let formCopy = this.prepareRegistrationForSaving(formValues);

      // Get basis registration info
      let registration_info = this.getBasicRegistrationInfo(formCopy)

      // Send the registration to the server, or save it to outbox if it can't be saved on the server.
      let sendRegistration = await this.saveRegistration(formCopy, true);
      if (!sendRegistration.success) {
        // Log and show errors
        Vue.prototype.$rollbar.error(sendRegistration.message, registration_info);
        this.$toast.error(sendRegistration.message, {timeout: false});
      }

      if (this.sendMethod === 'save-manual') {
        Vue.prototype.$rollbar.info('Eform registration completed saving to local device.', registration_info);

        this.$toast.success("Formulier is opgeslagen om later verzonden te worden");

        // Allow user to leave page without a confirm box
        this.unsavedChanges = false

        if (!this.form_in_modal) {
          this.$router.push({name: "unsend_registration_list"});
        }
        else {
          this.$emit('closeModal');
        }
      }

      this.savingModal = false
      this.savingForm = false
    },
    async onValidationErrors() {
      if (this.sendMethod === 'send') {
        this.$toast.error("Controleer het formulier. Sommige velden zijn niet of verkeerd ingevuld.", { timeout: false });
      }
    },
    startAutosave () {
      if (this.guest_form) {
        return; // Don't use autosave on guest forms
      }
      // Make sure this function is started once
      if (this.autoSavePolling) {
        return;
      }
      // Make sure the timestamp to start the autosaving has been elapsed.
      if (this.autoSaveStartPollingOnTimestamp === null || this.autoSaveStartPollingOnTimestamp > Date.now()) {
        return;
      }

      console.log('start autosave polling..')

      let $vm = this;
      this.autoSavePolling = setInterval(() => {
        // Call the auto save the registration (offline) every 5 seconds. This does not save the form, since it's checking inside that function if it's possible to save.
        $vm.autoSaveForm();
      }, 4000)
    },
    autoSaveForm() {
      let cooldownHasElapsed = Date.now() >= this.autoSaveCooldown;
      // Prevent auto-save when:
      // - An subform is opened
      // - The autosave cooldown has not elapsed yet, to make sure we don't autosave twice in rapid succession. For example: when the timer triggers the autosave ánd the close-subform-modal triggers the autosave.
      // - The form is saving/submitting online (this.savingForm)
      // - The form saving is completed (this.savingComplete)
      // - The form is loading
      // - The form is saving
      // - The form is hidden (this.renderForm must be true)
      if (!this.subformModalIsOpen && cooldownHasElapsed && !this.savingForm && !this.savingComplete && !this.loading && this.renderForm) {
        console.log('Autosave has started..')
        // Set time a cooldown time. When this time has elapsed its possible to save again.
        this.autoSaveCooldown = Date.now() + 6000; // Cooldown of 120 seconds
        // Save the registration
        this.saveRegistrationViaAutosave('fillForm');
        console.log('Autosave has completed')
      }
    },
    /**
     * Load the assigned registration
     *
     * @param registration_id
     */
    async loadAssignedRegistration(registration_id) {
      let registration = null;
      // Fetch results form the API
      await axios
          .get('api/assigned-registration/' + registration_id)
          .then(response => {
            registration = response.data;
          })
          .catch(error => {
            console.log(error)
          })
      return registration;
    },
    /**
     * Load a completed registration
     *
     * @param registration_id
     */
    async loadRegistration(registration_id) {
      let registration = null;
      // Fetch results form the API
      await axios
          .get('api/form/' + this.form_id + '/registrations/' + registration_id)
          .then(response => {
            registration = response.data;
          })
          .catch(error => {
            console.log(error)
          })
      return registration;
    },
    async initializeForm(form) {
      // Get form from API
      if (this.appOnline) {
        this.updateForm(form);
        // Check if form has unprocessed registrations
        await this.checkFormUnprocessedRegistrations()
        // Load client
        await this.loadClient(this.myForm.client_id)
        this.loadingForm = false;
      }
      else {
        // Make a copy of the form and set form to this.myForm
        this.myForm = cloneDeep(form)
        this.loadingForm = false;
      }

      // Load assigned workflow registration (edit an existing registration)
      if (this.registration_id) {

        let registration = null;

        // Check if this form is a workflow form
        if (this.myForm.form_type === 'workflow') {
          // Load registration
          this.loadingForm = true;
          registration = await this.loadAssignedRegistration(this.registration_id);
          this.loadingForm = false;
          // Set the 'registration_phase' from the registration
          this.myForm.registration_phase = registration.registration_phase;
          // // Add registration date, we need this on the backend to check if we are not overwriting an old registration onto a new registration.
          // this.myForm.registration_date = registration.registration_date;
          // Add registration serial number, we need this on the backend
          this.myForm.registration_serial_number = registration.registration_serial_number;
          // Set the 'original_registration_id' we are editing, used in the backend to identify the original registration we want to edit.
          this.myForm.original_registration_id = registration._id;
          // Set the fill_token from the loaded registration
          this.myForm.fill_token = registration.fill_token;
        }
        else {
          // Load registration (fill in again)
          this.loadingForm = true;
          registration = await this.loadRegistration(this.registration_id);
          // // New registration, create a new fill token
          this.myForm.fill_token = uuid.v4();
          this.loadingForm = false;
          console.log('loaded registration (fill in again)');
        }

        // Set form values
        this.setFormValues(registration)
      }
      // Set default values
      else if (this.saved_registration_id) {
        // We need to hide to form before setting the default values, otherwise the file and image values won't show.
        this.hideForm()
        // Load the saved registration
        let registration = await this.getSavedRegistration(this.saved_registration_id)
        // Set default values
        this.$nextTick(() => {
          // Set default values
          this.setFormValues(registration)
          // Use same fill token
          this.myForm.fill_token = registration.fill_token

          // Restore the 'registration_serial_number' for workflow registrations
          if (_.has(registration, 'registration_serial_number')) {
            this.myForm.registration_serial_number = _.get(registration, 'registration_serial_number');
          }
          if (_.has(registration, 'original_registration_id')) {
            this.myForm.original_registration_id = _.get(registration, 'original_registration_id');
          }
          if (_.has(registration, 'registration_phase')) {
            this.myForm.registration_phase = _.get(registration, 'registration_phase');
          }

          // Reshow the form
          this.$nextTick(() => this.showForm())
        })
      }
      else {
        // New registration, create a new fill token
        this.myForm.fill_token = uuid.v4();

        if (this.myForm.form_type === 'workflow') {
          // Set the initial registration phase for workflow forms
          this.myForm.registration_phase = '1';
        }
      }

      // Make sure the form has a fill_token, if not log an error (this should not happen)
      if (!this.myForm.fill_token) {
        console.error('Form fill has no fill_token assigned.', { form_id: form.form_id, form_name: form.form_name });
        Vue.prototype.$rollbar.error('Form fill has no fill_token assigned.', { form_id: form._id, form_name: form.form_name });
      }

      // Make sure the form has a fill_token, if not log an error (this should not happen)
      if (!this.myForm.fill_token) {
        console.error('Form fill has no fill_token assigned.', { form_id: form.form_id, form_name: form.form_name });
        Vue.prototype.$rollbar.error('Form fill has no fill_token assigned.', { form_id: form._id, form_name: form.form_name });
      }

    },
    setFormValues(registration) {
      this.loadingSavedRegistration = true;

      let values = registration.values;
      let schema = this.schema;

      if (this.myForm.form_type === 'workflow') {
        // Set the registration phase from the selected registration
        this.myForm.registration_phase = registration.registration_phase;
      }

      let myValues = _.clone(values);
      // Convert file values from multiple to single arrays
      this.fileValuesConvertMultipleToSingle(schema, myValues)

      if (this.$formulate.registry.has('fillForm')) {
        this.$formulate.reset('fillForm');
      }

      this.values = myValues;

      this.renderForm = true;
      this.loadingSavedRegistration = false;
    },
    getSavedRegistration(saved_registration_id) {
      // Load the registration from the server
      return axios
          .get('/api/saved-registrations/'+saved_registration_id)
          .then(response => {
            return response.data
          })
          .catch(error => {
            console.log(error)
          })
    },
    hideForm() {
      this.renderForm = false
    },
    showForm() {
      this.renderForm = true
    },
    setDisabled(item) {
      // Disable subform when saving
      if (this.savingForm) {
        return true
      }

      let appOnline = store.getters.appOnline;
      // Disable file widget when app is offline
      if (item.type === 'file' && appOnline === false) {
        return true
      }
    },
    isVisuallyShown(item) {
      if (item.visuallyHidden) {
        return false;
      }
      return true;
    },
    updateForm(form) {
      // Set default values
      this.myForm = this.formSetDefaultValues(form);
      // Unset the '_id', since this is the ID of the form. We don't want to send that to the server when creating a registration.
      delete this.myForm._id;
    },
    formSetDefaultValues(form) {
      if (Array.isArray(form.values)) {
        form.values = Object.fromEntries(form.values);
      }

      for (let i in form.schema) {
        // rememberInput is currently not stored for subforms.
        // if (form.schema[i].elementType === 'subform') {
        //     let subformDefaults = this.formSetDefaultValues(form.schema[i].subform, true);
        //     form.schema[i].subform = subformDefaults;
        //     continue;
        // }

        if (form.schema[i].rememberInput) {
          // Get previously saved input from vuex store
          let oldFieldValue = this.$store.getters.getFieldValue(form.schema[i].uuid);
          if (oldFieldValue !== undefined) {
            form.values[form.schema[i].name] = oldFieldValue;
          }
        }

        let dateTypeArray = ['date', 'time', 'datetime'];
        if (dateTypeArray.includes(form.schema[i].elementType) && form.schema[i].defaultDateNow) {
          switch (form.schema[i].elementType) {
            case 'date':
              form.values[form.schema[i].name] = moment().format("YYYY-MM-DD");
              break;
            case 'time':
              form.values[form.schema[i].name] = moment().format("HH:mm");

              break;
            case 'datetime':
              form.values[form.schema[i].name] = moment().format("yyyy-MM-DDTHH:mm");

              break;
          }
        }
      }
      return form;
    },
    prepareRegistrationForSaving(formValues) {
      // Make a copy of the formValues to remove cyclical values
      let formValuesCopy = cloneDeep(formValues);
      // Make sure file arrays have a multidimensional array (an array inside an array).
      // This ugly fix is needed because we can only set default values for a file input
      this.fileValuesConvertSingleToMultiple(this.schema, formValuesCopy)

      // Bugfix: Convert the Vue Formulate complex file uploads into simple data.
      for (const [key, element] of Object.entries(this.myForm.schema)) {
        if (element.elementType === 'subform') {
          // Remove all form values in the schema of the subforms (we don't need this over here).
          // And more important, it can have complex file-upload data in it, so that needs be removed before submitting.
          delete this.myForm.schema[key].subform.values
        }
      }

      // Make a copy of the form
      let formCopy = cloneDeep(this.myForm);
      // Override values with the formValues to prevent complex file data.
      formCopy.values = formValuesCopy;

      return formCopy;
    },
    async prepareAndSendRegistration(formValues, guest = false) {
      this.savingModal = true
      this.savingForm = true;
      this.$toast.clear();

      // Prepare the registration for saving
      let formCopy = this.prepareRegistrationForSaving(formValues);
      if (this.formType === 'workflow') {
        // Get the dataname of the visible state transition element (we need to do this in the frontend, because unfortunately the back-nd doesn't know what element is currently visible)
        formCopy.registration_visible_state_transition = this.getVisibleStateTransitionDatanameFromSchema();
      }

      // Get basis registration info
      let registration_info = this.getBasicRegistrationInfo(formCopy)

      let guest_token = null;
      if (guest) {
        // Get the guest token from the route
        guest_token = this.$route.params.guest_id || null;
      }

      // Send the registration to the server, or save it to outbox if it can't be saved on the server.
      let sendRegistration = await this.saveRegistration(formCopy, false, guest_token);

      if (!sendRegistration.success) {
        this.savingModal = false
        this.savingForm = false

        // Log and show errors
        Vue.prototype.$rollbar.error(sendRegistration.message, registration_info);
        this.$toast.error(sendRegistration.message, { timeout: false });
      }
      else {
        // Clean up the registration from the store and save some field values
        await this.registrationCompleted({registration: formCopy});

        Vue.prototype.$rollbar.info('Eform registration completed sending to server.', registration_info);
        // Allow user to leave page without a confirm box
        this.unsavedChanges = false
        // Check form action
        if (this.myForm.form_settings && this.myForm.form_settings.form_complete_action && this.myForm.form_settings.form_complete_action === 'fillFormAgain') {
          this.fillFormAgain()
        }
        else {
          // Execute normal form complete action
          this.savingModal = false
          this.savingForm = false;
          this.savingComplete = true
          // Emit event so we can show a success message on the FormFill.vue, FormFillGuest.vue or LinkedFormComponent.vue
          this.$emit('savingComplete');
        }
      }
    },
    /**
     * Get the visible state transition element dataname from the conditional schema
     */
    getVisibleStateTransitionDatanameFromSchema() {
      let element = this.conditionalSchema.filter(item => item.elementType === 'stateTransition');
      if (element.length > 0) {
        return element[0]['name'];
      }
      return null;
    },
    getBasicRegistrationInfo(registration) {
      return {
        registration_id: registration._id ?? '',
        fill_token: registration.fill_token ?? '',
        client_id: registration.client_id ?? '',
        form_name: registration.form_name ?? '',
        form_id: registration.form_id ?? '',
        form_type: registration.form_type ?? '',
      }
    },
    subformOpened() {
      this.subformModalIsOpen = true;
    },
    subformClosed() {
      this.subformModalIsOpen = false;
      // Disable the submit button temporarily
      this.setSubformClosedRecently();
      // Trigger autosave
      this.autoSaveForm();
    },
    setSubformClosedRecently() {
      // Set flag to indicate the subform has been closed recently. Used to disable the submit button temporarily.
      this.subformHasClosedRecently = true;
      // Reset the flag after a short delay
      setTimeout(() => {
        this.subformHasClosedRecently = false;
      }, 1000);
    },
    fillFormAgain() {
      if (!this.form_in_modal) {
        // Refresh page. Dont use this.$router.go() because it does not work on iOS.
        window.location.reload();
      }
    },
    confirmClose() {
      if (this.unsavedChanges) {
        return window.confirm('Wilt u deze pagina verlaten? Indien u wijzigingen heeft aangebracht gaan deze verloren.');
      }
      return true;
    },
    onGlobalKeydown(event) {
      // Only Ctrl (not Meta/Cmd) + U
      if (!event.ctrlKey || event.metaKey) return;
      if (event.key === 'u' || event.key === 'U') {
        event.preventDefault();
        this.saveRegistrationViaAutosave('fillForm');
      }
    }
  },
  beforeDestroy () {
    clearInterval(this.autoSavePolling)
  },
}
</script>

<style lang="scss">

// Use the compact style
@import '@/styles/formulate-compact.scss';

.v-card__actions .formulate-input {
  margin-bottom: 0em !important;
}
// Colors
$green: var(--v-success-base);
$primary: var(--v-primary-base);
$white: #fff;

</style>